import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Heading,
  Input,
  Select,
  HStack,
  Button,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Text,
  Badge,
  Flex,
  Spacer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";

import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import materialsService from "../services/materialsService";
import lookupsService from "../services/lookupsService";
import { useUser } from "../context/UserContext";
import { useNavigate } from "react-router-dom";

const statusOptions = ["ACTIVE", "INACTIVE", "BLOCKED"];

// Summary bar (replaces KPI cards)
function SummaryBar({ stats, activeFilter, onFilter }) {
  const pieces = [
    { key: "ALL", label: `Materials: ${stats.total || 0}` },
    { key: "ACTIVE", label: `Active: ${stats.active || 0}` },
    { key: "INACTIVE", label: `Inactive: ${stats.inactive || 0}` },
    { key: "BLOCKED", label: `Blocked: ${stats.blocked || 0}` },
  ];
  return (
    <HStack spacing={3} mb={3} fontSize="13px" color="gray.600">
      {pieces.map((p, idx) => (
        <Text
          key={p.key}
          onClick={() => onFilter(p.key === "ALL" ? "" : p.key)}
          cursor="pointer"
          sx={
            activeFilter === p.key
              ? { fontWeight: "bold", textDecoration: "underline" }
              : {}
          }
        >
          {p.label}
          {idx < pieces.length - 1 && (
            <Text as="span" mx={3} color="gray.400">
              •
            </Text>
          )}
        </Text>
      ))}
    </HStack>
  );
}

export const MaterialForm = ({
  initial = {},
  onSubmit,
  onCancel,
  isOpen,
  onClose,
  inline = false,
  categories = [],
  brands = [],
  uoms = [],
  taxCategories = [],
  onRefreshLookups = () => {},
}) => {
  const [form, setForm] = useState({
    materialCode: "",
    barcode: "",
    name: "",
    shortName: "",
    categoryId: "",
    brandId: "",
    materialType: "PRODUCT",
    uomId: "",
    isStockItem: true,
    isBatchTracked: false,
    isSerialTracked: false,
    isWeighable: false,
    isReturnable: true,
    isDiscountAllowed: true,
    taxCategoryId: "",
    hsnCode: "",
    taxInclusive: false,
    costPrice: 0,
    mrp: 0,
    baseSellingPrice: 0,
    status: "ACTIVE",
    allowNegativeStock: false,
    ...initial,
  });

  useEffect(() => setForm((s) => ({ ...s, ...initial })), [initial]);

  const handleCreateLookup = async (type) => {
    // open a small modal and resolve once user confirms
    if (!type) return null;
    return new Promise((resolve) => {
      createResolveRef.current = resolve;
      setCreateLookupName("");
      setCreateLookupType(type);
      onCreateOpen();
    });
  };

  // create modal state/refs
  const [createLookupType, setCreateLookupType] = useState(null);
  const [createLookupName, setCreateLookupName] = useState("");
  const createResolveRef = useRef(null);
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  const confirmCreateLookup = async () => {
    const name = (createLookupName || "").trim();
    if (!name) {
      // resolve with null to caller
      if (createResolveRef.current) createResolveRef.current(null);
      onCreateClose();
      return;
    }
    try {
      let created = null;
      if (createLookupType === "category")
        created = await lookupsService.createCategory({ name });
      if (createLookupType === "brand")
        created = await lookupsService.createBrand({ name });
      if (createLookupType === "uom")
        created = await lookupsService.createUOM({ name });
      if (createLookupType === "tax")
        created = await lookupsService.createTaxCategory({ name });
      await onRefreshLookups();
      if (createResolveRef.current) createResolveRef.current(created);
    } catch (e) {
      console.error("failed creating lookup", e);
      if (createResolveRef.current) createResolveRef.current(null);
    } finally {
      onCreateClose();
    }
  };

  const saveDisabled = (f) => {
    const formCheck = f || {};
    const required = [
      "materialCode",
      "name",
      "categoryId",
      "materialType",
      "uomId",
      "taxCategoryId",
      "mrp",
      "baseSellingPrice",
    ];
    try {
      return (
        !required.every((k) => {
          const v = formCheck[k];
          if (v === undefined || v === null) return false;
          if (typeof v === "string") return v.trim() !== "";
          if (typeof v === "number") return !isNaN(v);
          return true;
        }) ||
        Number(formCheck.mrp) < 0 ||
        Number(formCheck.baseSellingPrice) < 0
      );
    } catch (e) {
      return true;
    }
  };

  const fillTestData = () => {
    const suffix = String(Date.now()).slice(-5);
    setForm((prev) => ({
      ...prev,
      materialCode: `TEST-${suffix}`,
      barcode: `BC${suffix}`,
      name: `Test Material ${suffix}`,
      shortName: `TM${suffix}`,
      categoryId:
        (categories &&
          categories[0] &&
          (categories[0]._id || categories[0].id)) ||
        "",
      brandId: (brands && brands[0] && (brands[0]._id || brands[0].id)) || "",
      uomId: (uoms && uoms[0] && (uoms[0]._id || uoms[0].id)) || "",
      taxCategoryId:
        (taxCategories &&
          taxCategories[0] &&
          (taxCategories[0]._id || taxCategories[0].id)) ||
        "",
      materialType: "PRODUCT",
      mrp: 100,
      baseSellingPrice: 120,
      costPrice: 80,
      status: "ACTIVE",
      isStockItem: true,
      isReturnable: true,
      isDiscountAllowed: true,
    }));
  };

  const FormInner = (
    <VStack spacing={3} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} width="100%">
        <FormControl>
          <FormLabel>Code</FormLabel>
          <Input
            value={form.materialCode || ""}
            onChange={(e) => setForm({ ...form, materialCode: e.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Barcode</FormLabel>
          <Input
            value={form.barcode || ""}
            onChange={(e) => setForm({ ...form, barcode: e.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Name</FormLabel>
          <Input
            value={form.name || ""}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Short Name</FormLabel>
          <Input
            value={form.shortName || ""}
            onChange={(e) => setForm({ ...form, shortName: e.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>
            Category
            <Button
              size="xs"
              ml={3}
              onClick={async () => {
                const created = await handleCreateLookup("category");
                if (created)
                  setForm({ ...form, categoryId: created._id || created.id });
              }}
            >
              New
            </Button>
          </FormLabel>
          <Select
            value={form.categoryId || ""}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
          >
            <option value="">Select category</option>
            {(categories || []).map((c) => (
              <option key={c._id || c.id} value={c._id || c.id}>
                {c.name || c.title || c.label || c._id || c.id}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>
            Brand
            <Button
              size="xs"
              ml={3}
              onClick={async () => {
                const created = await handleCreateLookup("brand");
                if (created)
                  setForm({ ...form, brandId: created._id || created.id });
              }}
            >
              New
            </Button>
          </FormLabel>
          <Select
            value={form.brandId || ""}
            onChange={(e) => setForm({ ...form, brandId: e.target.value })}
          >
            <option value="">(none)</option>
            {(brands || []).map((b) => (
              <option key={b._id || b.id} value={b._id || b.id}>
                {b.name || b.title || b.label || b._id || b.id}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Material Type</FormLabel>
          <Select
            value={form.materialType}
            onChange={(e) => setForm({ ...form, materialType: e.target.value })}
          >
            <option value="PRODUCT">PRODUCT</option>
            <option value="SERVICE">SERVICE</option>
            <option value="CONSUMABLE">CONSUMABLE</option>
            <option value="BUNDLE">BUNDLE</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>
            UOM
            <Button
              size="xs"
              ml={3}
              onClick={async () => {
                const created = await handleCreateLookup("uom");
                if (created)
                  setForm({ ...form, uomId: created._id || created.id });
              }}
            >
              New
            </Button>
          </FormLabel>
          <Select
            value={form.uomId || ""}
            onChange={(e) => setForm({ ...form, uomId: e.target.value })}
          >
            <option value="">Select UOM</option>
            {(uoms || []).map((u) => (
              <option key={u._id || u.id} value={u._id || u.id}>
                {u.name || u.title || u.label || u._id || u.id}
              </option>
            ))}
          </Select>
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} width="100%">
        <FormControl>
          <FormLabel>MRP</FormLabel>
          <NumberInput
            min={0}
            value={form.mrp || 0}
            onChange={(v) => setForm({ ...form, mrp: Number(v) })}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel>Cost Price</FormLabel>
          <NumberInput
            min={0}
            value={form.costPrice || 0}
            onChange={(v) => setForm({ ...form, costPrice: Number(v) })}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel>Base Selling Price</FormLabel>
          <NumberInput
            min={0}
            value={form.baseSellingPrice || 0}
            onChange={(v) => setForm({ ...form, baseSellingPrice: Number(v) })}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} width="100%">
        <FormControl>
          <FormLabel>
            Tax Category
            <Button
              size="xs"
              ml={3}
              onClick={async () => {
                const created = await handleCreateLookup("tax");
                if (created)
                  setForm({
                    ...form,
                    taxCategoryId: created._id || created.id,
                  });
              }}
            >
              New
            </Button>
          </FormLabel>
          <Select
            value={form.taxCategoryId || ""}
            onChange={(e) =>
              setForm({ ...form, taxCategoryId: e.target.value })
            }
          >
            <option value="">Select tax category</option>
            {(taxCategories || []).map((t) => (
              <option key={t._id || t.id} value={t._id || t.id}>
                {t.name || t.title || t.label || t._id || t.id}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>HSN Code</FormLabel>
          <Input
            value={form.hsnCode || ""}
            onChange={(e) => setForm({ ...form, hsnCode: e.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Tax Inclusive</FormLabel>
          <Select
            value={String(form.taxInclusive)}
            onChange={(e) =>
              setForm({ ...form, taxInclusive: e.target.value === "true" })
            }
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} width="100%">
        <FormControl>
          <FormLabel>Stock Item</FormLabel>
          <Select
            value={String(form.isStockItem)}
            onChange={(e) =>
              setForm({ ...form, isStockItem: e.target.value === "true" })
            }
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Batch Tracked</FormLabel>
          <Select
            value={String(form.isBatchTracked)}
            onChange={(e) =>
              setForm({ ...form, isBatchTracked: e.target.value === "true" })
            }
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Serial Tracked</FormLabel>
          <Select
            value={String(form.isSerialTracked)}
            onChange={(e) =>
              setForm({ ...form, isSerialTracked: e.target.value === "true" })
            }
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} width="100%">
        <FormControl>
          <FormLabel>Weighable</FormLabel>
          <Select
            value={String(form.isWeighable)}
            onChange={(e) =>
              setForm({ ...form, isWeighable: e.target.value === "true" })
            }
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Returnable</FormLabel>
          <Select
            value={String(form.isReturnable)}
            onChange={(e) =>
              setForm({ ...form, isReturnable: e.target.value === "true" })
            }
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Discount Allowed</FormLabel>
          <Select
            value={String(form.isDiscountAllowed)}
            onChange={(e) =>
              setForm({ ...form, isDiscountAllowed: e.target.value === "true" })
            }
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel>Allow Negative Stock</FormLabel>
        <Select
          value={String(form.allowNegativeStock)}
          onChange={(e) =>
            setForm({ ...form, allowNegativeStock: e.target.value === "true" })
          }
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel>Status</FormLabel>
        <Select
          value={form.status}
          onChange={(e) => setForm({ ...form, status: e.target.value })}
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
      </FormControl>
    </VStack>
  );

  // Create lookup modal JSX
  const CreateLookupModal = (
    <Modal
      isOpen={isCreateOpen}
      onClose={() => {
        if (createResolveRef.current) createResolveRef.current(null);
        onCreateClose();
      }}
      isCentered
      size="sm"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create {createLookupType}</ModalHeader>
        <ModalBody>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              value={createLookupName}
              onChange={(e) => setCreateLookupName(e.target.value)}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={() => {
              if (createResolveRef.current) createResolveRef.current(null);
              onCreateClose();
            }}
          >
            Cancel
          </Button>
          <Button colorScheme="teal" onClick={confirmCreateLookup}>
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  if (inline) {
    return (
      <Box>
        {FormInner}
        {CreateLookupModal}
        <HStack mt={3} justify="flex-end">
          <Button variant="outline" onClick={fillTestData} mr={2}>
            Fill Test Data
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={() => onSubmit(form)}
            isDisabled={saveDisabled(form)}
          >
            Save
          </Button>
        </HStack>
      </Box>
    );
  }
  return (
    <>
      {CreateLookupModal}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {initial && (initial._id ? "Edit Material" : "New Material")}
          </ModalHeader>
          <ModalBody>{FormInner}</ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={fillTestData}>
              Fill Test Data
            </Button>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              onClick={() => onSubmit(form)}
              isDisabled={saveDisabled(form)}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
const MaterialMaster = () => {
  const [materials, setMaterials] = useState([]);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [taxCategories, setTaxCategories] = useState([]);
  const [query, setQuery] = useState({ search: "", status: "" });
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const cancelRef = React.useRef();
  const { tenant } = useUser();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [inlineMode, setInlineMode] = useState(false);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const searchRef = React.useRef(null);

  async function load() {
    setLoading(true);
    try {
      // fetch a reasonable set; UI will filter client-side
      const res = await materialsService.listMaterials({ limit: 1000 });
      const items = Array.isArray(res) ? res : res.items || res.data || [];
      setMaterials(items);
      const total = items.length;
      const active = items.filter((i) => i.status === "ACTIVE").length;
      const inactive = items.filter((i) => i.status === "INACTIVE").length;
      const blocked = items.filter((i) => i.status === "BLOCKED").length;
      setStats({ total, active, inactive, blocked });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadLookups() {
    try {
      const [cats, brs, u, taxes] = await Promise.all([
        lookupsService.getCategories(),
        lookupsService.getBrands(),
        lookupsService.getUOMs(),
        lookupsService.getTaxCategories(),
      ]);
      setCategories(cats);
      setBrands(brs);
      setUoms(u);
      setTaxCategories(taxes);
    } catch (e) {
      console.warn("Failed to load lookups", e);
    }
  }

  useEffect(() => {
    load();
    loadLookups();
  }, []);

  // debounce search input into query.search
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery((q) => ({ ...q, search: searchInput }));
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // keyboard shortcuts: '/' -> focus search, 'N' -> new material
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "/" && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current && searchRef.current.focus();
      }
      if ((e.key === "N" || e.key === "n") && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        openNew();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const openNew = () => {
    setSelected(null);
    navigate("/materials/new");
  };
  const openEdit = (m) => {
    setSelected(m);
    navigate(`/materials/${m._id}/edit`);
  };

  const handleSave = async (payload) => {
    try {
      const payloadToSend = { ...payload };
      [
        "_id",
        "__v",
        "createdBy",
        "updatedBy",
        "deletedAt",
        "createdAt",
        "updatedAt",
      ].forEach((k) => delete payloadToSend[k]);

      if (tenant && (tenant._id || tenant.id)) {
        payloadToSend.tenantId = tenant._id || tenant.id;
      }

      if (selected && selected._id) {
        await materialsService.updateMaterial(selected._id, payloadToSend);
      } else {
        await materialsService.createMaterial(payloadToSend);
      }
      onClose();
      setInlineMode(false);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDuplicate = async (m) => {
    try {
      const copy = { ...m };
      delete copy._id;
      copy.materialCode = `${m.materialCode || "COPY"}-COPY`;
      copy.name = `${m.name} (Copy)`;
      await materialsService.createMaterial(copy);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleBlock = async (m) => {
    try {
      const newStatus = m.status === "BLOCKED" ? "ACTIVE" : "BLOCKED";
      await materialsService.updateMaterial(m._id, { status: newStatus });
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const confirmDelete = (m) => {
    setDeleteTarget(m);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await materialsService.deleteMaterial(deleteTarget._id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setQuery({ search: "", status: "" });
    setPage(1);
  };

  // client-side filtering
  const filtered = materials.filter((m) => {
    const s = (query.search || "").trim().toLowerCase();
    const status = (query.status || "").toUpperCase();
    let ok = true;
    if (s) {
      const hay = `${m.name || ""} ${m.materialCode || ""} ${
        m.barcode || ""
      }`.toLowerCase();
      ok = hay.indexOf(s) !== -1;
    }
    if (ok && status) ok = (m.status || "").toUpperCase() === status;
    return ok;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Box p={6}>
      <Box mb={2}>
        <Heading size="md">Materials</Heading>
        <Text fontSize="13px" color="gray.500">
          Create and manage items used in sales and inventory
        </Text>
      </Box>

      <SummaryBar
        stats={stats}
        activeFilter={query.status || "ALL"}
        onFilter={(statusKey) => setQuery((q) => ({ ...q, status: statusKey }))}
      />

      <Flex align="center" mb={3}>
        <HStack spacing={3}>
          <Input
            placeholder="Search materials"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            ref={searchRef}
            width="360px"
            size="sm"
          />
          <Select
            value={query.status}
            onChange={(e) =>
              setQuery((q) => ({ ...q, status: e.target.value }))
            }
            width="160px"
            size="sm"
          >
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="BLOCKED">Blocked</option>
          </Select>
          {(query.search || query.status) && (
            <Button size="sm" variant="ghost" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </HStack>
        <Spacer />
        <Button
          leftIcon={<FiPlus />}
          colorScheme="teal"
          size="sm"
          onClick={openNew}
        >
          New Material
        </Button>
      </Flex>

      <Box borderWidth={1} borderRadius="6px" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr
              style={{
                position: "sticky",
                top: 0,
                background: "white",
                zIndex: 1,
              }}
            >
              <Th>SKU</Th>
              <Th>Name</Th>
              <Th textAlign="right">MRP</Th>
              <Th textAlign="right">Cost Price</Th>
              <Th>Status</Th>
              <Th textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {visible.map((m) => (
              <Tr
                key={m._id}
                onMouseEnter={() => setHoveredRow(m._id)}
                onMouseLeave={() => setHoveredRow(null)}
                onClick={() => openEdit(m)}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openEdit(m)}
                _hover={{ bg: "gray.50", cursor: "pointer" }}
                height="44px"
              >
                <Td>
                  <Text fontFamily="mono" color="gray.600">
                    {m.materialCode}
                  </Text>
                </Td>
                <Td>
                  <Text fontWeight="medium">{m.name}</Text>
                  <Text fontSize="12px" color="gray.500">
                    {m.shortName}
                  </Text>
                </Td>
                <Td textAlign="right">
                  <Text style={{ fontVariantNumeric: "tabular-nums" }}>
                    {m.mrp}
                  </Text>
                </Td>
                <Td textAlign="right">
                  <Text style={{ fontVariantNumeric: "tabular-nums" }}>
                    {m.baseSellingPrice}
                  </Text>
                </Td>
                <Td>
                  <Flex align="center">
                    <Box
                      width="8px"
                      height="8px"
                      borderRadius="50%"
                      mr={2}
                      bg={
                        m.status === "ACTIVE"
                          ? "green.400"
                          : m.status === "INACTIVE"
                          ? "yellow.400"
                          : "red.400"
                      }
                      aria-hidden
                    />
                    <Text>{m.status}</Text>
                  </Flex>
                </Td>
                <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                  <Box visibility={hoveredRow === m._id ? "visible" : "hidden"}>
                    <Menu>
                      <MenuButton as={Button} size="sm">
                        ⋮
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={() => openEdit(m)}>Edit</MenuItem>
                        <MenuItem onClick={() => handleDuplicate(m)}>
                          Duplicate
                        </MenuItem>
                        <MenuItem onClick={() => handleToggleBlock(m)}>
                          {m.status === "BLOCKED" ? "Unblock" : "Block"}
                        </MenuItem>
                        <MenuItem onClick={() => confirmDelete(m)}>
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Box>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <Box p={8} textAlign="center">
            <Heading size="sm" mb={2}>
              No materials yet
            </Heading>
            <Text color="gray.600" mb={4}>
              Create materials to manage pricing, inventory, and availability.
            </Text>
            <Button colorScheme="teal" onClick={openNew}>
              New Material
            </Button>
          </Box>
        )}

        {/* Footer / pagination */}
        <Flex
          align="center"
          justify="space-between"
          p={3}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <HStack>
            <Select
              size="sm"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Select>
            <Text fontSize="13px" color="gray.600">
              Rows per page
            </Text>
          </HStack>
          <Text fontSize="13px" color="gray.600">
            Page {page} of {totalPages}
          </Text>
        </Flex>
      </Box>

      {/* Delete confirmation modal */}
      <AlertDialog
        isOpen={!!deleteTarget}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteTarget(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete material
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete "
              {deleteTarget && deleteTarget.name}"? This action cannot be
              undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={doDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <MaterialForm
        initial={selected || {}}
        onSubmit={handleSave}
        isOpen={isOpen && !inlineMode}
        onClose={() => {
          setInlineMode(false);
          onClose();
        }}
        categories={categories}
        brands={brands}
        uoms={uoms}
        taxCategories={taxCategories}
      />
    </Box>
  );
};

export default MaterialMaster;
